/**
 * Coletele pe ecranul Rambursuri curieri: tabelul, panoul lateral cu drumul coletului si
 * inchiderea cu motiv (12.10.2026).
 */
import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { Pastila } from '../../../components/ui/Pastila'
import { PanouLateral, SectiunePanou } from '../../../components/ui/PanouLateral'
import { Traseu } from '../../../components/ui/Traseu'
import { LinkAwb } from '../../../components/ui/LinkAwb'
import { LivratFaraScanare } from '../../../components/ui/LivratFaraScanare'
import { EtichetaUrgentare } from './urgentare'
import type { Ton } from '../../../components/ui/TonuriB4L'
import {
  type Expeditie,
  type ExpeditieDetaliu,
  STATUS_LABEL,
  lei,
  mesajServer,
  numeCurier,
  zi,
} from './tipuri'

const cand = (s: string) =>
  new Date(s).toLocaleString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

/** Eticheta de stare a coletului, in tonul ei. Intarzierea bate starea: e ce cere atentie. */
export function StareColet({ e }: { e: Expeditie }) {
  if (e.intarziere != null) return <Pastila ton="rosu">{e.intarziere} zile lucrătoare</Pastila>
  const ton: Record<string, Ton> = {
    livrat: 'verde',
    in_tranzit: 'albastru',
    in_retur: 'galben',
    refuzat: 'galben',
    retur: 'galben',
    nepreluat: 'gri',
    inchis: 'gri',
    inlocuit: 'gri',
  }
  return <Pastila ton={ton[e.stare ?? ''] ?? 'gri'}>{e.etichetaStare ?? 'În curs'}</Pastila>
}

export function TabelColete({
  randuri,
  onDeschide,
  coloanaZile = 'Stare',
}: {
  randuri: Expeditie[]
  onDeschide: (id: number) => void
  coloanaZile?: string
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-b4l-border text-left text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
            <th className="px-3 py-2">AWB</th>
            <th className="px-3 py-2">Client · comandă</th>
            <th className="px-3 py-2">Preluat</th>
            <th className="px-3 py-2">Ultima mișcare</th>
            <th className="px-3 py-2">{coloanaZile}</th>
          </tr>
        </thead>
        <tbody>
          {randuri.map((e) => (
            <tr
              key={e.id}
              onClick={() => onDeschide(e.id)}
              className="cursor-pointer border-b border-b4l-border align-top last:border-0 hover:bg-b4l-bg-soft"
            >
              <td className="px-3 py-2.5">
                <div>
                  <LinkAwb curier={e.courier} awb={e.awbNo} className="font-mono text-xs" />
                </div>
                <div className="text-xs text-b4l-text-dim">{numeCurier(e.courier)}</div>
              </td>
              <td className="px-3 py-2.5">
                <div className="font-medium">{e.recipientName ?? '—'}</div>
                <div className="text-xs text-b4l-text-dim">
                  {[e.recipientLocality, e.comandaNo].filter(Boolean).join(' · ')}
                </div>
              </td>
              <td className="px-3 py-2.5 tabular-nums">
                {e.pickedUpAt ? zi(e.pickedUpAt) : '—'}
                {e.pickedUpApprox && <span className="text-xs text-b4l-text-dim"> (estimat)</span>}
              </td>
              <td className="px-3 py-2.5">
                {/*
                  Livrat: confirmarea de primire, nu ultima scanare. Curierul nu scaneaza mereu
                  „Livrat" (AWB 7000173560101: ultima scanare „preluata spre livrare", dar semnat
                  de destinatar pe 16.10) — iar randul parea ramas pe drum (21.10.2026).
                */}
                {e.stare === 'livrat' && e.deliveredAt ? (
                  <>
                    <div className="flex items-center gap-1">
                      Livrat
                      {e.faraScanareLivrare && <LivratFaraScanare semnat={e.deliveredTo} />}
                    </div>
                    <div className="text-xs text-b4l-text-dim">
                      {cand(e.deliveredAt)}
                      {e.deliveredTo ? ` · semnat de ${e.deliveredTo}` : ''}
                    </div>
                  </>
                ) : e.stare === 'inlocuit' && e.closeNote ? (
                  <>
                    <div>Plecat pe alt AWB</div>
                    <div className="text-xs text-b4l-text-dim">{e.closeNote}</div>
                  </>
                ) : e.lastEventName ? (
                  <>
                    <div>{e.lastEventName}</div>
                    <div className="text-xs text-b4l-text-dim">
                      {e.lastEventAt ? cand(e.lastEventAt) : ''}
                      {e.lastEventLocation ? ` · ${e.lastEventLocation}` : ''}
                    </div>
                  </>
                ) : (
                  <span className="text-xs text-b4l-text-dim">
                    AWB emis {zi(e.awbDate)} — curierul nu l-a ridicat încă
                  </span>
                )}
              </td>
              <td className="px-3 py-2.5">
                <StareColet e={e} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Panoul din dreapta: drumul coletului, comanda si incasarea rambursului. */
export function PanouColet({ id, onInchide }: { id: number | null; onInchide: () => void }) {
  const canWrite = useAuthStore((s) => s.permissions).includes('fan:write')
  const [inchidere, setInchidere] = useState(false)
  const { data: c, isError: eroare } = useQuery<ExpeditieDetaliu>({
    queryKey: ['fan', 'expeditie', id],
    queryFn: async () => (await api.get(`/fan/expeditii/${id}`)).data,
    enabled: id != null,
  })

  // Aceeasi regula ca ruta de inchidere (#800): un colet livrat, inchis sau intr-o stare finala
  // nu se mai inchide de mana — nici cand starea n-a apucat sa se recalculeze.
  const deschisa =
    !!c &&
    !c.closedAt &&
    !c.deliveredAt &&
    ['nepreluat', 'in_tranzit', 'in_retur', 'refuzat'].includes(c.stare ?? '')
  return (
    <PanouLateral
      deschis={id != null}
      onInchide={onInchide}
      titlu={
        c ? <LinkAwb curier={c.courier} awb={c.awbNo} /> : <span className="font-mono">…</span>
      }
      subtitlu={
        c && (
          <>
            <StareColet e={c} />
            {c.blockedSince && <Pastila ton="galben">fără mișcare de 4+ zile</Pastila>}
            <EtichetaUrgentare courier={c.courier} urgentari={c.urgentari} />
          </>
        )
      }
    >
      {eroare ? (
        <p className="text-sm text-b4l-red-dark">Nu am putut aduce coletul. Încearcă din nou.</p>
      ) : !c ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
      ) : (
        <>
          <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-b4l-text-dim">Curier</dt>
            <dd>{numeCurier(c.courier)}</dd>
            <dt className="text-b4l-text-dim">Client</dt>
            <dd>{[c.recipientName, c.recipientLocality].filter(Boolean).join(', ') || '—'}</dd>
            <dt className="text-b4l-text-dim">Comandă</dt>
            <dd>
              {c.comandaShopify ? (
                `${c.comandaShopify.name} · Shopify`
              ) : c.comandaCrm ? (
                <Link
                  className="text-b4l-blue-dark underline"
                  to={`/operational/comenzi/${c.comandaCrm.id}`}
                >
                  {c.comandaCrm.code}
                </Link>
              ) : (
                (c.comandaNo ?? '—')
              )}
            </dd>
            <dt className="text-b4l-text-dim">AWB emis</dt>
            <dd className="tabular-nums">{zi(c.awbDate)}</dd>
            <dt className="text-b4l-text-dim">Preluat</dt>
            <dd className="tabular-nums">
              {c.pickedUpAt ? zi(c.pickedUpAt) : 'nepreluat încă'}
              {c.pickedUpApprox && ' (estimat)'}
            </dd>
            {c.deliveredAt && (
              <>
                <dt className="text-b4l-text-dim">Livrat</dt>
                <dd className="tabular-nums">
                  {cand(c.deliveredAt)}
                  {c.deliveredTo && `, semnat de ${c.deliveredTo}`}
                  {c.faraScanareLivrare && <LivratFaraScanare semnat={c.deliveredTo} text />}
                </dd>
              </>
            )}
            {c.urgentari?.map((u) => (
              <Fragment key={u.fel}>
                <dt className="text-b4l-text-dim">
                  {u.fel === 'cerut' ? 'Urgentare' : 'Necerut curierului'}
                </dt>
                <dd>
                  {u.fel === 'cerut'
                    ? `cerută pe ${zi(u.createdAt)}${u.trimitere ? ` la ${u.trimitere.catre.join(', ')}` : ''}`
                    : u.motiv}
                </dd>
              </Fragment>
            ))}
            {/* Doar pe un colet inchis: Sameday il redeschide la o miscare noua, dar lasa nota. */}
            {c.closedAt && c.closeNote && (
              <>
                <dt className="text-b4l-text-dim">
                  {c.stare === 'inlocuit' ? 'Plecat pe' : 'Închis pentru'}
                </dt>
                <dd>{c.closeNote}</dd>
              </>
            )}
          </dl>

          <SectiunePanou titlu="Drumul coletului">
            <Traseu
              pasi={c.evenimente.map((e) => ({ cand: e.at, titlu: e.name, loc: e.location }))}
              gol="Curierul n-a raportat încă nicio mișcare."
            />
          </SectiunePanou>

          <SectiunePanou titlu="Încasarea rambursului">
            {c.incasari.length === 0 ? (
              <p className="text-sm text-b4l-text-muted">
                Curierul n-a virat încă banii pentru acest colet.
              </p>
            ) : (
              c.incasari.map((i) => (
                <dl
                  key={i.id}
                  className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm"
                >
                  <dt className="text-b4l-text-dim">Virat pe</dt>
                  <dd className="tabular-nums">{zi(i.transferDate)}</dd>
                  <dt className="text-b4l-text-dim">Sumă</dt>
                  <dd className="tabular-nums">{lei(i.amountCollected)} lei</dd>
                  <dt className="text-b4l-text-dim">Factură</dt>
                  <dd className="font-mono text-xs">{i.eaDocNo ?? '—'}</dd>
                  <dt className="text-b4l-text-dim">Stare</dt>
                  <dd>
                    <Pastila ton={i.postedAt ? 'verde' : 'albastru'}>
                      {STATUS_LABEL[i.status] ?? i.status}
                    </Pastila>
                  </dd>
                </dl>
              ))
            )}
          </SectiunePanou>

          {deschisa && canWrite && (
            <div>
              <Button variant="secondary" onClick={() => setInchidere(true)}>
                Închide coletul…
              </Button>
              <p className="mt-1.5 text-xs text-b4l-text-muted">
                Pentru colete anulate sau rezolvate altfel. Se redeschide singur dacă se mișcă din
                nou.
              </p>
            </div>
          )}
          {/* Nu si la „inlocuit": regula l-ar inchide din nou la urmatoarea sincronizare. */}
          {c.stare === 'inchis' && canWrite && <Redeschide id={c.id} />}
          {inchidere && <InchideColet id={c.id} awb={c.awbNo} onGata={() => setInchidere(false)} />}
        </>
      )}
    </PanouLateral>
  )
}

/** Redeschide un colet inchis de mana din greseala. Starea se recalculeaza pe loc in API. */
function Redeschide({ id }: { id: number }) {
  const qc = useQueryClient()
  const m = useMutation({
    mutationFn: async () => (await api.post(`/fan/expeditii/${id}/redeschide`, {})).data,
    onSuccess: () => {
      toast.success('Coletul a fost redeschis')
      qc.invalidateQueries({ queryKey: ['fan'] })
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Coletul nu s-a putut redeschide.')),
  })
  return (
    <div>
      <Button variant="secondary" onClick={() => m.mutate()} loading={m.isPending}>
        Redeschide coletul
      </Button>
      <p className="mt-1.5 text-xs text-b4l-text-muted">
        Revine în liste și în alerte, cu starea dată de curier.
      </p>
    </div>
  )
}

/** Inchiderea unui colet, cu motiv obligatoriu — in locul ferestrei native a browserului. */
function InchideColet({ id, awb, onGata }: { id: number; awb: string; onGata: () => void }) {
  const qc = useQueryClient()
  const [motiv, setMotiv] = useState('')
  const m = useMutation({
    mutationFn: async () => (await api.post(`/fan/expeditii/${id}/inchide`, { motiv })).data,
    onSuccess: () => {
      toast.success('Coletul a fost închis')
      qc.invalidateQueries({ queryKey: ['fan'] })
      onGata()
    },
    // Serverul refuza un colet deja livrat/returnat/anulat si spune de ce (#800).
    onError: (e: unknown) => toast.error(mesajServer(e, 'Coletul nu s-a putut închide.')),
  })
  return (
    <Modal
      open
      onClose={onGata}
      title={`Închide coletul ${awb}`}
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={onGata}>
            Renunță
          </Button>
          <Button
            onClick={() => m.mutate()}
            loading={m.isPending}
            disabled={motiv.trim().length < 3}
          >
            Închide coletul
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-b4l-text-muted">
        Coletul iese din listele de lucru și din alerte. Motivul rămâne scris pe colet.
      </p>
      <label htmlFor="motiv-inchidere" className="mb-1 block text-xs font-medium">
        Motivul
      </label>
      <textarea
        id="motiv-inchidere"
        rows={3}
        value={motiv}
        onChange={(e) => setMotiv(e.target.value)}
        placeholder="ex. Comanda anulată de client, AWB nefolosit"
        className="w-full rounded border border-b4l-border px-3 py-2"
      />
    </Modal>
  )
}
